import { AppDispatch, RootState } from '../../store';
import { accountStateSelector } from '../account/api';
import { bibleDB } from '../bible/database';
import { removeVersion } from '../bible/functions';
import { loadPlaylistsForAccount } from '../playlist/functions';
import { playlistActions } from '../playlist/slice';
import { PluginContextType } from '../plugin/types';
import { saveResource } from '../resources/actions';
import { fixKnownLegacyIssues } from '../resources/functions';
import { resourceActions } from '../resources/slice';
import { Resource } from '../resources/types';
import { migrateFromPreviousVersion } from '../settings/functions';
import { defaultTheme } from '../themes/constants';
import { themeActions } from '../themes/slice';
import { uiActions } from '../ui/slice';
import { actions } from './slice';
import { BootstrapStage } from './types';

type ActionsType = typeof actions;

type StageImpl = {
  perform: (pluginContext: PluginContextType, dispatch: AppDispatch, actions: ActionsType, state: RootState) => void
}

type MaybeLegacyThemes = Record<string, object> | { themes: Record<string, object> }

export const STAGES: Record<BootstrapStage, StageImpl> = {
  [BootstrapStage.LOCATE_PLUGIN]: {
    perform: (pluginContext, dispatch, actions) => {
      dispatch(actions.setCaption('Connecting to plugin...'));
      pluginContext.findAndConnect().then(
        () => dispatch(actions.nextStage())
      ).catch(
        () => {
          // Plugin not found!
          dispatch(uiActions.setCurrentModal({ modal: 'pluginInstallRequest' }));
          dispatch(actions.loadFailed());
        }
      );
    }
  },
  [BootstrapStage.LOAD_RESOURCES]: {
    perform: (pluginContext, dispatch, actions, state) => {
      dispatch(actions.setCaption('Loading resources...'));
      const accountState = accountStateSelector(state);
      pluginContext.loadResources(accountState.data!.id).then(
        (resources) => {
          dispatch(resourceActions.bulkImport(resources || {}));

          // Do some post-processing to repair some known issues with legacy data
          dispatch(actions.setCaption('Post-processing...'));

          for (const resource of Object.values((resources || {}) as Resource[])) {
            const repairedResource = fixKnownLegacyIssues(resource as Resource);
            if (repairedResource !== resource) {
              // By dispatching these as individual addResource actions, we
              // make sure the changes end up in the sync queue and eventually
              // pushed to the server.
              dispatch(saveResource(repairedResource));
            }
          }

          dispatch(actions.setCaption('Building search index...'));
          dispatch(resourceActions.rebuildIndex());

          dispatch(actions.nextStage());
        }
      );
    }
  },
  [BootstrapStage.LOAD_THEMES]: {
    perform: (pluginContext, dispatch, actions, state) => {
      dispatch(actions.setCaption('Loading themes...'));
      const accountState = accountStateSelector(state);
      pluginContext.loadThemes(accountState.data!.id).then(
        (themes: unknown) => {
          const prevThemes = themes as MaybeLegacyThemes;
          let imported = 0;
          if (prevThemes?.themes) {
            dispatch(themeActions.bulkImport(prevThemes.themes));
            imported = Object.keys(prevThemes.themes).length;
          }
          else {
            dispatch(themeActions.bulkImport(prevThemes || {}));
            imported = Object.keys(prevThemes || {}).length;
          }
          if (imported === 0) {
            dispatch(themeActions.saveTheme(defaultTheme));
          }

          if (!state.ui.currentThemeID) {
            // Make sure we always have a theme selected
            const arbitraryTheme = Object.values(state.themes)[0];
            dispatch(uiActions.setCurrentThemeID(arbitraryTheme?.id || defaultTheme.id));
          }

          dispatch(actions.nextStage());
        }
      );
    }
  },
  [BootstrapStage.INIT_BIBLES]: {
    perform: (_, dispatch, actions) => {
      dispatch(actions.setCaption('Loading Bibles...'));
      bibleDB.versions.filter(v => v.download_state !== 'complete').toArray().then(
        async versions => {
          for (const version of versions) {
            await removeVersion(version.id);
          }

          dispatch(actions.nextStage());
        }
      );
    }
  },
  [BootstrapStage.OPEN_OUTPUT_WINDOWS]: {
    perform: (pluginContext, dispatch, actions, state) => {

      new Promise<void>(
        (resolve) => {
          if (state.settings.outputWindows.length === 0) {
            dispatch(actions.setCaption('Migrating previous output window settings...'));
            migrateFromPreviousVersion(dispatch);
            resolve();
          }
          else {
            resolve();
          }
        }
      ).then(
        () => {
          dispatch(actions.setCaption('Opening output windows...'));
          state.settings.outputWindows.forEach(
            (window, idx) => {
              pluginContext.openOutputWindow(window, state.settings.display, idx);
            }
          );
          dispatch(actions.nextStage());
        }
      );

    }
  },
  [BootstrapStage.LOAD_PLAYLISTS]: {
    perform: (_, dispatch, actions, state) => {
      dispatch(actions.setCaption('Loading playlists...'));

      // By this point we really should have an account ID!
      const accountState = accountStateSelector(state);
      const accountID = accountState.data!.id;

      loadPlaylistsForAccount(accountID).then(
        playlists => {
          dispatch(playlistActions.import(playlists));
          dispatch(actions.nextStage());
        }
      );

    }
  },
  [BootstrapStage.READY]: {
    perform: (_, dispatch, actions) => {
      dispatch(actions.nextStage());
      dispatch(actions.setCaption('Ready!'));
      setTimeout(
        () => dispatch(actions.isReady()),
        500
      );
    }
  }
};
